import cn from 'classnames';
import { useSpring, animated, config } from 'react-spring';
import { IBlackjackRoom, TMaxPlayers, TRoomSlug } from '@api/types/blackjack';
import { IChipItem, ROOM } from '@consts/blackjack';
import { getActivePlayersInRoom } from '@utils/blackjack';
import Container from '@components/container';
import styles from './room-data.module.scss';
import ActivePlayersCount from '@components/active-players-count';

export interface IRoomDataProps {
  room: IBlackjackRoom;
  className?: string;
}

const RoomData = ({
  room: { max_players, current_game, recent_games },
  className = '',
}: IRoomDataProps): JSX.Element => {
  const containerColor: TRoomSlug = ROOM.COLORS[max_players as TMaxPlayers];
  const Chip: IChipItem = ROOM.CHIPS[max_players as TMaxPlayers];
  const roomName: string = ROOM.NAMES[max_players as TMaxPlayers];

  const activePlayers: number = getActivePlayersInRoom({
    current_game,
    recent_games,
  });

  /* Animations */

  const chipSpring = useSpring({
    from: { y: -100, scale: 0.5, opacity: 0, rotate: 1080 },
    to: { y: 0, scale: 1, opacity: 1, rotate: 0 },
    delay: 1000,
    config: config.slow,
  });

  const infoSpring = useSpring({
    from: { opacity: 0, y: 50, scale: 0.5 },
    to: { opacity: 1, y: 1, scale: 1 },
    delay: 1000,
    config: config.gentle,
  });

  /* ClassNames */

  const containerClasses = cn(styles.roomData, className, {
    [styles[`roomData-color-${containerColor}`]]: containerColor,
  });

  return (
    <Container className={containerClasses} radius="sm">
      <div className={styles.chip}>
        <animated.figure style={chipSpring} className={styles.chip__icon}>
          {Chip.Icon}
        </animated.figure>
        <div className={styles.chip__number}>{Chip.Number}</div>
      </div>
      <animated.section style={infoSpring} className={styles.info}>
        <div className={styles.info__room}>
          <h3>{roomName} room</h3>
          <p>{max_players} Players</p>
        </div>
        <ActivePlayersCount
          className={styles.info__activePlayers}
          {...{ activePlayers }}
        />
      </animated.section>
    </Container>
  );
};

export default RoomData;
